// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 14-local-development.md
// @greenlight-spec: 10-security-compliance.md#local-dev-cli-session
// @greenlight-spec: decisions/0028-agent-cli-mcp-independent-client.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';

import { Logo } from '@/components/greenlight/Logo';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';

/**
 * Loopback sign-in landing (ADR-0028). The standalone `--loopback` flow
 * 302-redirects the browser here after capturing the auth code — no code or token
 * travels in the redirect. Rendered unauthenticated, outside the AppShell, at the
 * SPA route `/cli/done`, so it reuses the dashboard design system instead of
 * hand-rolled HTML.
 *
 * It is not the pairing-success screen: there is no `cli_sessions` row behind
 * this page and nothing to disclose, so it stays static, fetch-free, and
 * path-agnostic. The person ran the flow from their coding agent, so the copy
 * sends them back there; the dashboard link is a secondary affordance.
 */
export function CliSignedIn(): React.ReactElement {
  React.useEffect(() => {
    document.title = 'Plugin signed in · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12">
      {/* Single blue radial halo — the brand's one atmospheric flourish (matches /login). */}
      <div
        aria-hidden
        className="pointer-events-none fixed -top-[200px] left-1/2 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(circle,hsl(var(--signal)/0.14)_0%,transparent_62%)]"
      />

      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      <main className="relative z-[1] flex w-full max-w-[408px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift Engineering
          </div>
        </div>

        <div className="w-full rounded-gl-lg border border-border bg-card p-6 text-center shadow-xl sm:p-8">
          <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-glow">
            <CheckCircle2 className="size-6 text-verdant" aria-hidden />
          </div>
          <Eyebrow>Greenlight plugin</Eyebrow>
          <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
            The Greenlight plugin is signed in
          </h1>
          <p className="mb-6 mt-2 text-sm leading-relaxed text-muted-foreground">
            You can close this tab and return to your agent.
          </p>
          <Button asChild variant="outline" className="h-11 w-full">
            <Link to="/">Open dashboard</Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
